import { Textures } from 'algu-domain'
import type { Texture } from 'three'
import { RepeatWrapping, SRGBColorSpace, WebGLRenderer } from 'three'

/**
 * Get full path to resource (for web component support)
 * @param path relative path to resource
 * @returns
 */
export const getResourceUrl = (path: string) => `${phoenix.runtimeConfig.RESOURCES_URL}${path}`

const renderer = new WebGLRenderer()
const maxAnisotropy = renderer.capabilities.getMaxAnisotropy()

const configureSteelNonColorTextures = (texture: Texture) => {
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.repeat.set(6, 6)
  texture.anisotropy = maxAnisotropy
  return texture
}

const configureWoodNonColorTextures = (texture: Texture) => {
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.repeat.set(3, 3)
  texture.anisotropy = maxAnisotropy
  return texture
}

const configureWoodColorTextures = (texture: Texture) => {
  const tempTexture = configureWoodNonColorTextures(texture)
  tempTexture.colorSpace = SRGBColorSpace
  return texture
}

const configureCeramicNonColorTextures = (texture: Texture) => {
  texture.rotation = Math.PI * 0.5
  texture.anisotropy = maxAnisotropy
  return texture
}

const configureCeramicColorTextures = (texture: Texture) => {
  const tempTexture = configureCeramicNonColorTextures(texture)
  tempTexture.colorSpace = SRGBColorSpace
  return texture
}

export const TextureProcessingMap: Record<string, (texture: Texture) => Texture> = {
  [Textures.TOP_LIVED_OAK_BASE_COLOR_URL]: configureWoodNonColorTextures,
  [Textures.TOP_LIVED_OAK_NORMAL_URL]: configureWoodNonColorTextures,
  [Textures.TOP_LIVED_OAK_ROUGHNESS_URL]: configureWoodNonColorTextures,
  [Textures.TOP_REFINED_OAK_BASE_COLOR_URL]: configureWoodColorTextures,
  [Textures.TOP_REFINED_OAK_NORMAL_URL]: configureWoodNonColorTextures,
  [Textures.TOP_REFINED_OAK_ROUGHNESS_URL]: configureWoodNonColorTextures,
  [Textures.TOP_OAK_BASE_COLOR_URL]: configureWoodColorTextures,
  [Textures.TOP_OAK_NORMAL_URL]: configureWoodNonColorTextures,
  [Textures.TOP_CERAMIC_ROUGHNESS_URL]: configureCeramicNonColorTextures,
  [Textures.STEEL_NORMAL_URL]: configureSteelNonColorTextures,
  [Textures.STEEL_ROUGHNESS_URL]: configureSteelNonColorTextures,
  [Textures.BasicNieve]: configureCeramicColorTextures,
  [Textures.BiancoSupreme]: configureCeramicColorTextures,
  [Textures.BlueStoneNegro]: configureCeramicColorTextures,
  [Textures.CalacataGold]: configureCeramicColorTextures,
  [Textures.CalacataNaturel]: configureCeramicColorTextures,
  [Textures.CalacataVaglie]: configureCeramicColorTextures,
  [Textures.CalceGrigio]: configureCeramicColorTextures,
  [Textures.CeppoDeGre]: configureCeramicColorTextures,
  [Textures.ClassicCarrara]: configureCeramicColorTextures,
  [Textures.CuarzoReno]: configureCeramicColorTextures,
  [Textures.DarkBlack]: configureCeramicColorTextures,
  [Textures.HyperWhite]: configureCeramicColorTextures,
  [Textures.LavaCorten]: configureCeramicColorTextures,
  [Textures.MacchiaVecchia]: configureCeramicColorTextures,
  [Textures.NeroArdi]: configureCeramicColorTextures,
  [Textures.NeroZimbabwe]: configureCeramicColorTextures,
  [Textures.NoirDesir]: configureCeramicColorTextures,
  [Textures.PaladioNaturel]: configureCeramicColorTextures,
  [Textures.SurfaceCoolGrey]: configureCeramicColorTextures,
  [Textures.SurfaceOffWhite]: configureCeramicColorTextures,
  [Textures.SurfaceSand]: configureCeramicColorTextures,
  [Textures.TajMahal]: configureCeramicColorTextures,
  [Textures.TitanCemento]: configureCeramicColorTextures,
  [Textures.Travertin]: configureCeramicColorTextures,
}

export const TEXTURE_QUERY_CACHE_OPTIONS = {
  retryOnMount: true, // SDK default is false, this is the react-query default
  retry: 3, // SDK default is false, this is the react-query default
} as const
